import React, {useEffect} from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import {CreditCard, TrendingUp, BarChart3} from 'lucide-react';
import {usePaymentsManagement} from '../hooks/usePaymentsManagement';
import PaymentsTable from '../components/PaymentsTable';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import PaymentFilters from '../components/PaymentFilters';
import PaymentStatistics from '../components/PaymentStatistics';
import BankTransferReviewTable from '../components/BankTransferReviewTable';
import {useSalesDashboard} from '../../sales/hooks/useSalesDashboard';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

interface TabConfig {
  key: 'payments' | 'transfers' | 'statistics';
  label: string;
  icon: React.ReactNode;
  description: string;
  count?: number;
  permission?: string;      // Single permission
  permissions?: string[];   // Multiple permissions (OR logic)
}

const PaymentCollectionPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  // Unified payments management
  const {
    // UI State
    activeTab,

    // Data
    payments, statistics, paymentsPagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleTabChange, handleFilterChange, handleClearFilters,
    handlePageChange, handlePageSizeChange
  } = usePaymentsManagement();

  // Dashboard sales hook (pending transfers/aging)
  const {pendingTransfers, fetchPendingTransfers, reviewBankTransfer} = useSalesDashboard();

  // Load initial data
  useEffect(() => {
    try {
      // Dashboard sales bootstrapping
      fetchPendingTransfers({page: 1, limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE});
    } catch (error) {
      // Error handling is done in the hooks
    }
  }, [fetchPendingTransfers]);

  // Check if user has permission to view payments
  if (!hasPermission(PERMISSIONS.BILLING_MANAGE) &&
      !hasPermission(PERMISSIONS.BILLING_APPROVE) &&
      !hasPermission(PERMISSIONS.VIEW_FINANCIAL_REPORTS)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة التحصيلات. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }


  const tabs: TabConfig[] = [
    {
      key: 'payments' as const,
      label: 'سجل التحصيلات',
      icon: <CreditCard className="w-4 h-4" />,
      count: (payments || []).length,
      description: 'عرض سجل جميع المدفوعات والتحصيلات',
      permission: PERMISSIONS.BILLING_MANAGE
    },
    {
      key: 'transfers' as const,
      label: 'مراجعة التحويلات',
      icon: <BarChart3 className="w-4 h-4" />,
      description: 'التحويلات البنكية قيد المراجعة',
      permissions: [PERMISSIONS.BILLING_MANAGE, PERMISSIONS.BILLING_APPROVE] // OR logic - user needs ANY of these
    },
    {
      key: 'statistics' as const,
      label: 'إحصائيات التحصيلات',
      icon: <TrendingUp className="w-4 h-4" />,
      description: 'إحصائيات شاملة للتحصيلات والمدفوعات',
      permission: PERMISSIONS.VIEW_FINANCIAL_REPORTS
    }
  ];

  // Filter tabs based on permissions - supports both single permission and permissions array (OR logic)
  const visibleTabs = tabs.filter(tab => {
    // If no permission required, show tab
    if (!tab.permission && !tab.permissions) {return true;}

    // Check single permission
    if (tab.permission) {return hasPermission(tab.permission);}

    // Check multiple permissions (OR logic - user needs ANY of these)
    if (tab.permissions) {
      return tab.permissions.some(perm => hasPermission(perm));
    }

    return false;
  });

  // Tab click handler is now provided by the unified hook

  // Filter handlers are now provided by the unified hook


  const renderPaymentsTab = () => (
    <div className="space-y-6">
      {/* Payments Filters */}
      <PaymentFilters
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Payments Table */}
      <PaymentsTable
        payments={payments}
        loading={loading}
        paginatedData={{
          data: payments,
          pagination: paymentsPagination
        }}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        exportable={true}
      />
    </div>
  );


  const renderStatisticsTab = () => (
    <div className="space-y-6">
      <PaymentStatistics statistics={statistics} loading={loading} />
    </div>
  );

  const renderTransfersTab = () => (
    <div className="space-y-6">
      <BankTransferReviewTable
        data={pendingTransfers || []}
        onApprove={async (invoiceId) => {
          await reviewBankTransfer({invoice_id: Number(invoiceId), action: 'approved'});
          // Refresh the data after successful approval
          fetchPendingTransfers({page: 1, limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE});
        }}
        onReject={async (invoiceId) => {
          await reviewBankTransfer({invoice_id: Number(invoiceId), action: 'rejected'});
          // Refresh the data after successful rejection
          fetchPendingTransfers({page: 1, limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE});
        }}
        onRefresh={() => fetchPendingTransfers({page: 1, limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE})}
        exportable={true}
      />
    </div>
  );

  return (
    <SidebarLayout>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <AppTitle>إدارة التحصيلات</AppTitle>
            <p className="text-gray-600 mt-1">إدارة شاملة للمدفوعات وتتبع التحصيلات</p>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <CreditCard className="w-4 h-4 mr-1" />
            نظام إدارة التحصيلات المتكامل
          </div>
        </div>

        {/* Error Messages */}
        {error && (
          <Card className="p-4 bg-red-50 border border-red-200">
            <div className="text-red-700">{error}</div>
          </Card>
        )}

        {/* Tabs Navigation */}
        <Card className="p-0 overflow-hidden">
          <div className="flex border-b border-gray-200 bg-gray-50">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'bg-white text-brand-600 border-b-2 border-brand-600'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                <div className="flex flex-col items-start">
                  <span>{tab.label}</span>
                  {tab.description && (
                    <span className="text-xs text-gray-500 font-normal">{tab.description}</span>
                  )}
                </div>
                {tab.count !== undefined && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.key ? 'bg-brand-100 text-brand-600' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'payments' && renderPaymentsTab()}
            {activeTab === 'transfers' && renderTransfersTab()}
            {activeTab === 'statistics' && renderStatisticsTab()}
          </div>
        </Card>
      </div>
    </SidebarLayout>
  );
};

export default PaymentCollectionPage;
